import React, { useState, useEffect } from 'react';
import { formatNumber, formatCurrency, formatDate } from '../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../components/AuthContext';
import { getAccessibleModules, isAdmin, isSuperuser } from '../utils/permissions';
import CurrentStatusSummary from './modules/accounting/cashflow/CurrentStatusSummary';
import axios from 'axios';
import DashboardWidget from '../components/DashboardWidget';
import ActivityStream from '../components/ActivityStream';
import QuickActions from '../components/QuickActions';
import OnboardingChecklist from '../components/OnboardingChecklist';
import { isNativeApp } from '../utils/native';
import { canOpenPath } from '../utils/routeAccess';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function DashboardPageNew() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  // The phone app keeps the home screen short: the module cards only (no
  // summary row, activity stream, shortcuts, notifications, licence or help).
  const COMPACT = isNativeApp();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [widgets, setWidgets] = useState([]);
  const [quickActions, setQuickActions] = useState([]);
  const [summary, setSummary] = useState({});
  const [activities, setActivities] = useState([]);
  const [widgetData, setWidgetData] = useState({});
  const [mostVisitedPages, setMostVisitedPages] = useState([]);

  // Also refetch on language change: activity and notification texts come
  // translated from the backend (Accept-Language).
  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [user, i18n.language]);

  const fetchDashboardData = async () => {
    try {
      // Get available widgets and actions
      const widgetsResponse = await axios.get(`${API_URL}/api/dashboard/widgets`);
      setWidgets(widgetsResponse.data.widgets || []);
      setQuickActions(widgetsResponse.data.quick_actions || []);

      // Get summary
      const summaryResponse = await axios.get(`${API_URL}/api/dashboard/summary`);
      setSummary(summaryResponse.data);

      // Recent activities: administrators only (not shown in the app)
      if (isAdmin(user) && !COMPACT) {
        const activitiesResponse = await axios.get(`${API_URL}/api/dashboard/activities`);
        setActivities(activitiesResponse.data.activities || []);
      }

      // Most visited pages feed the right column (web only); notifications
      // live in the header bell
      if (!COMPACT) {
        // Get most visited pages for personalized quick actions
        try {
          const mostVisitedResponse = await axios.get(`${API_URL}/api/page-visits/most-visited`);
          setMostVisitedPages(mostVisitedResponse.data.most_visited || []);
        } catch (error) {
          console.debug('Failed to fetch most visited pages:', error);
        }
      }

      // Fetch widget data for each available widget
      const widgetDataPromises = widgetsResponse.data.widgets.map(async (widget) => {
        try {
          const response = await axios.get(`${API_URL}/api/dashboard/widget/${widget}`);
          return { [widget]: response.data };
        } catch (error) {
          console.error(`Failed to fetch widget data for ${widget}:`, error);
          return { [widget]: null };
        }
      });

      const widgetDataResults = await Promise.all(widgetDataPromises);
      const widgetDataObj = widgetDataResults.reduce((acc, curr) => ({ ...acc, ...curr }), {});
      setWidgetData(widgetDataObj);

    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getWidgetConfig = (moduleName, data) => {
    const configs = {
      sales: {
        title: t('shared.moduleNames.sales'),
        value: formatCurrency(data?.this_month_total || 0),
        // F32: the same figure as Satış Dashboard's "Bu Ay", and it says so
        subtitle: `${t('dashboard.widgetSubtitles.salesBasis')} · ${t('dashboard.widgetSubtitles.sales', { count: data?.pending_orders || 0 })}`,
        icon: '💰',
        color: 'blue',
        // only a real change gets an arrow ("stable" drew a red ↓ 0%)
        trend: (data?.trend === 'up' || data?.trend === 'down') && data?.trend_percentage
          ? { direction: data.trend, percentage: data.trend_percentage } : null,
        onClick: () => navigate('/modules/sales')
      },
      // P2-22: the company headcount only for those who can open the employee list
      hr: canOpenPath(user, '/modules/hr/employees') ? {
        title: t('shared.moduleNames.hr'),
        value: data?.total_employees || 0,
        subtitle: t('dashboard.totalEmployees'),
        icon: '👥',
        color: 'green',
        onClick: () => navigate('/modules/hr')
      } : {
        title: t('shared.moduleNames.hr'),
        value: t('modules.hr.myLeaves'),
        subtitle: '',
        icon: '👥',
        color: 'green',
        onClick: () => navigate('/modules/hr/my-leaves')
      },
      inventory: {
        title: t('shared.moduleNames.inventory'),
        value: data?.total_products || 0,
        subtitle: t('dashboard.widgetSubtitles.inventory', { count: data?.low_stock_items || 0 }),
        icon: '📦',
        color: 'purple',
        onClick: () => navigate('/modules/inventory')
      },
      finance: {
        // FIN-20: what the bank and cash accounts hold (Finans' own screen shows them apart)
        title: `${t('shared.moduleNames.finance')} · ${t('dashboard.financeCashBank')}`,
        value: data?.balance_by_currency?.length > 0
          ? data.balance_by_currency.map(c => `${formatNumber(c.amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${c.symbol}`).join(' • ')
          : formatCurrency(data?.balance || 0),
        subtitle: t('dashboard.widgetSubtitles.finance', { count: data?.pending_payments || 0 }),
        icon: '💳',
        color: 'orange',
        details: data?.balance_by_currency?.length > 0 ? data.balance_by_currency.map(c => ({
          label: c.currency,
          value: `${c.amount >= 0 ? '+' : ''}${formatNumber(c.amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${c.symbol}`,
          highlight: c.amount < 0
        })) : null,
        onClick: () => navigate('/modules/finance')
      },
      service: {
        title: t('shared.moduleNames.service'),
        value: data?.total_tickets || 0,
        subtitle: t('dashboard.widgetSubtitles.service', { count: data?.open_tickets || 0 }),
        icon: '🔧',
        color: 'indigo',
        onClick: () => navigate('/modules/service')
      },
      purchasing: {
        title: t('shared.moduleNames.purchasing'),
        value: data?.total_orders || 0,
        // approval phase 4: what waits for the reader's own decision
        subtitle: [t('dashboard.widgetSubtitles.purchasing', { count: data?.pending_orders || 0 }),
          data?.pending_for_me ? t('dashboard.widgetSubtitles.pendingForMe', { n: String(data.pending_for_me) }) : null]
          .filter(Boolean).join(' · '),
        icon: '🛒',
        color: 'teal',
        onClick: () => navigate('/modules/purchasing')
      },
      tender: {
        title: t('shared.moduleNames.tender'),
        value: data?.total_tenders || 0,
        subtitle: t('dashboard.widgetSubtitles.tender', { count: data?.active_tenders || 0 }),
        icon: '⚖️',
        color: 'amber',
        onClick: () => navigate('/modules/tender')
      },
      accounting: {
        // invoice counts only: what is to collect and to pay is in the
        // Güncel Durum summary above, so the card does not repeat it
        title: t('shared.moduleNames.accounting'),
        value: data?.total_invoices || 0,
        subtitle: t('dashboard.widgetSubtitles.accounting', { count: data?.this_month_count || 0 }),
        icon: '📊',
        color: 'emerald',
        onClick: () => navigate('/modules/accounting')
      },
      production: {
        title: t('shared.moduleNames.production'),
        value: data?.total_orders || 0,
        subtitle: t('dashboard.widgetSubtitles.production', { count: data?.in_progress || 0 }),
        icon: '🏭',
        color: 'slate',
        onClick: () => navigate('/modules/production')
      },
      assets: {
        title: t('shared.moduleNames.assets'),
        value: data?.total_assets || 0,
        subtitle: t('dashboard.widgetSubtitles.assets', { count: data?.assigned || 0 }),
        icon: '🏢',
        color: 'violet',
        onClick: () => navigate('/modules/assets')
      },
      tasks: {
        title: t('shared.moduleNames.tasks'),
        value: data?.total_tasks || 0,
        subtitle: t('dashboard.widgetSubtitles.tasks', { high: data?.high_priority || 0, overdue: data?.overdue_mine ?? data?.overdue ?? 0 }),
        icon: '✅',
        color: 'sky',
        details: (() => {
          const details = [];

          // Assigned to me
          if (data?.assigned_to_me) {
            details.push({
              label: `📥 ${t('dashboard.tasks.assignedToMe')}`,
              value: t('dashboard.tasks.summaryProgress', { total: data.assigned_to_me.total, inProgress: data.assigned_to_me.in_progress, overdue: data.assigned_to_me.overdue }),
              highlight: data.assigned_to_me.overdue > 0
            });
          }

          // Assigned by me
          if (data?.assigned_by_me) {
            details.push({
              label: `📤 ${t('dashboard.tasks.assignedByMe')}`,
              value: t('dashboard.tasks.summaryProgress', { total: data.assigned_by_me.total, inProgress: data.assigned_by_me.in_progress, overdue: data.assigned_by_me.overdue }),
              highlight: data.assigned_by_me.overdue > 0
            });
          }

          // All tasks for admin
          if (data?.all_tasks) {
            details.push({
              label: `🌐 ${t('dashboard.tasks.allTasks')}`,
              value: t('dashboard.tasks.summaryDone', { total: data.all_tasks.total, done: data.all_tasks.done })
            });
          }

          return details;
        })(),
        onClick: () => navigate('/modules/tasks/kanban')
      },
      office: {
        title: t('shared.moduleNames.office'),
        value: data?.total_announcements || 0,
        subtitle: t('dashboard.widgetSubtitles.office', { count: data?.today_bookings || 0 }),
        icon: '📄',
        color: 'cyan',
        onClick: () => navigate('/modules/office')
      },
      reservations: {
        title: t('shared.moduleNames.reservations'),
        value: data?.occupied_today || 0,
        subtitle: t('dashboard.widgetSubtitles.reservations', { count: data?.arrivals_7_days || 0 }),
        icon: '🗓️',
        color: 'cyan',
        onClick: () => navigate('/modules/reservations')
      },
      insurance: {
        title: t('shared.moduleNames.insurance'),
        value: data?.active_policies || 0,
        subtitle: t('dashboard.widgetSubtitles.insurance', { count: data?.expiring_30_days || 0 }),
        icon: '🛡️',
        color: 'indigo',
        onClick: () => navigate('/modules/insurance')
      },
      education: {
        title: t('shared.moduleNames.education'),
        value: data?.active_enrollments || 0,
        subtitle: t('dashboard.widgetSubtitles.education', { count: data?.active_courses || 0 }),
        icon: '🎓',
        color: 'purple',
        onClick: () => navigate('/modules/education')
      },
      membership: {
        title: t('shared.moduleNames.membership'),
        value: data?.active_memberships || 0,
        subtitle: t('dashboard.widgetSubtitles.membership', { count: data?.today_checkins || 0 }),
        icon: '🎟️',
        color: 'rose',
        onClick: () => navigate('/modules/membership')
      },
      ledger: {
        title: t('shared.moduleNames.ledger'),
        value: data?.accounts || 0,
        subtitle: t('dashboard.widgetSubtitles.ledger'),
        icon: '📒',
        color: 'indigo',
        onClick: () => navigate('/modules/ledger')
      },
      projects: {
        title: t('shared.moduleNames.projects'),
        value: data?.open_projects || 0,
        subtitle: t('dashboard.widgetSubtitles.projects', { amount: formatCurrency(data?.expected_profit || 0) }),
        icon: '🏗️',
        color: 'amber',
        onClick: () => navigate('/modules/projects')
      },
      crm: {
        title: t('shared.moduleNames.crm'),
        value: data?.total_leads || 0,
        subtitle: t('dashboard.widgetSubtitles.crm', { count: data?.hot_leads || 0 }),
        icon: '🤝',
        color: 'rose',
        onClick: () => navigate('/modules/crm')
      },
      companies: {
        title: t('dashboard.widgets.companies'),
        value: summary.total_companies || 0,
        subtitle: t('dashboard.totalCompany'),
        icon: '🏢',
        color: 'blue',
        onClick: () => navigate('/companies')
      },
      licenses: {
        title: t('dashboard.widgets.licenses'),
        value: t('dashboard.active'),
        subtitle: summary.license_expiry ? `${summary.license_expiry}` : '',
        icon: '🔑',
        color: 'yellow',
        onClick: () => navigate('/licenses')
      },
      system_health: {
        title: t('dashboard.widgets.systemHealth'),
        value: data?.status === 'healthy' ? `✅ ${t('dashboard.systemStatus.healthy')}` : (data?.status || t('dashboard.systemStatus.check')),
        subtitle: data?.uptime ? `${t('dashboard.systemStatus.uptime')}: ${data.uptime}` : '',
        icon: '🖥️',
        color: 'green',
        onClick: () => navigate('/system-health')
      }
    };

    // Map old module names to new ones
    const moduleNameMap = {
      'manufacturing': 'production'
    };
    const mappedName = moduleNameMap[moduleName] || moduleName;

    return configs[mappedName] || {
      title: moduleName,
      value: '-',
      icon: '📊',
      color: 'blue',
      onClick: () => navigate(`/modules/${moduleName}`)
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div>
        <h1 className="page-title text-3xl mb-2">
          🏠 {t('dashboard.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {t('dashboard.greeting', { name: user?.username || '' })}
          {user?.company_name && ` • ${user.company_name}`}
        </p>
      </div>

      {/* Onboarding hint — auto-hides when not in trial / all done */}
      {isAdmin(user) && !isSuperuser(user) && <OnboardingChecklist />}

      {/* Superuser Panel Button */}
      {isSuperuser(user) && (
        <div
          onClick={() => navigate('/superuser')}
          className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl shadow-lg p-6 cursor-pointer hover:shadow-2xl transition-all transform hover:scale-[1.02] group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-white dark:bg-gray-800 bg-opacity-20 rounded-full p-4 group-hover:bg-opacity-30 transition">
                <span className="text-4xl">👑</span>
              </div>
              <div className="text-white">
                <h2 className="text-2xl font-bold mb-1">{t('dashboard.superuserPanel')}</h2>
                <p className="text-white text-opacity-90">{t('dashboard.superuserDesc')}</p>
              </div>
            </div>
            <div className="text-white opacity-75 group-hover:opacity-100 transition">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </div>
      )}


      {/* Güncel Durum in short (the details are on the Ön Muhasebe page) */}
      {!isSuperuser(user) && getAccessibleModules(user).includes('accounting') && <CurrentStatusSummary />}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Widgets (2/3) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Module Widgets */}
          {widgets.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {widgets.map((widget) => {
                const config = getWidgetConfig(widget, widgetData[widget]);
                return (
                  <DashboardWidget
                    key={widget}
                    {...config}
                  />
                );
              })}
            </div>
          ) : (
            user?.role === 'user' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                <div className="text-4xl mb-3">⚠️</div>
                <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                  {t('dashboard.positionNotAssigned')}
                </h3>
                <p className="text-yellow-700">
                  {t('dashboard.positionNotAssignedDesc')}
                </p>
              </div>
            )
          )}

          {/* Activity Stream */}
          {/* who did what in the company: for administrators only */}
          {isAdmin(user) && !COMPACT && <ActivityStream activities={activities} />}
        </div>

        {/* Right Column - Quick Actions */}
        {!COMPACT && (
        <div className="space-y-6">
          <QuickActions actions={quickActions} mostVisitedPages={mostVisitedPages} canOpen={(path) => canOpenPath(user, path)} />

          {/* License Info Card */}
          {isAdmin(user) && summary.license_info && (
            <div className={`bg-gradient-to-br rounded-xl shadow-lg p-6 text-white ${
              summary.license_info.status_color === 'green' ? 'from-green-500 to-emerald-600' :
              summary.license_info.status_color === 'yellow' ? 'from-yellow-500 to-orange-600' :
              summary.license_info.status_color === 'red' ? 'from-red-500 to-rose-600' : 'from-purple-500 to-indigo-600'
            }`}>
              <h3 className="text-lg font-semibold mb-3">🔑 {t('dashboard.licenseInfo')}</h3>

              <div className="space-y-2 text-sm">
                {/* License Type */}
                <div className="flex justify-between items-center">
                  <span className="opacity-80">{t('dashboard.licenseType')}:</span>
                  <span className="font-semibold">
                    {summary.license_info.is_trial ? t('dashboard.licenseTypes.trial') :
                     summary.license_info.license_type === 'saas_monthly' ? `${t('dashboard.licenseTypes.monthly')} SaaS` :
                     summary.license_info.license_type === 'saas_yearly' ? `${t('dashboard.licenseTypes.yearly')} SaaS` :
                     summary.license_info.license_type === 'saas_lifetime' ? `${t('dashboard.licenseTypes.lifetime')} SaaS` :
                     summary.license_info.license_type === 'on_premise' ? t('dashboard.licenseTypes.onPremise') : '-'}
                  </span>
                </div>

                {/* User Limit */}
                {summary.license_info.user_limit && (
                  <div className="flex justify-between items-center">
                    <span className="opacity-80">{t('dashboard.userLimit')}:</span>
                    <span className="font-semibold">{summary.license_info.user_limit}</span>
                  </div>
                )}

                {/* Active Modules */}
                {summary.license_info.modules && (
                  <div className="flex justify-between items-center">
                    <span className="opacity-80">{t('dashboard.activeModule')}:</span>
                    <span className="font-semibold">{summary.license_info.modules.length}</span>
                  </div>
                )}

                {/* End Date */}
                {summary.license_info.end_date && (
                  <div className="flex justify-between items-center">
                    <span className="opacity-80">{t('dashboard.endDate')}:</span>
                    <span className="font-semibold">
                      {formatDate(summary.license_info.end_date)}
                    </span>
                  </div>
                )}

                {/* Days Remaining */}
                {summary.license_info.days_until_expiry !== null && summary.license_info.days_until_expiry !== undefined && (
                  <div className="flex justify-between items-center">
                    <span className="opacity-80">{t('dashboard.daysRemaining')}:</span>
                    <span className={`font-bold ${summary.license_info.days_until_expiry <= 7 ? 'text-white' : ''}`}>
                      {summary.license_info.days_until_expiry > 0 ? summary.license_info.days_until_expiry : t('dashboard.expired')}
                    </span>
                  </div>
                )}

                {/* Auto Renew Status */}
                <div className="flex justify-between items-center">
                  <span className="opacity-80">{t('dashboard.autoRenew')}:</span>
                  <span className="font-semibold">{summary.license_info.auto_renew ? t('dashboard.on') : t('dashboard.off')}</span>
                </div>
              </div>

              {/* License Key - masked */}
              {summary.license_info.license_key && (
                <div className="mt-3 pt-3 border-t border-white border-opacity-20">
                  <p className="text-xs opacity-70">{t('dashboard.licenseKey')}</p>
                  <p className="font-mono text-sm">{summary.license_info.license_key.substring(0, 10)}...{summary.license_info.license_key.slice(-5)}</p>
                </div>
              )}

              <button
                onClick={() => navigate('/licenses')}
                className="mt-4 w-full bg-white dark:bg-gray-800 bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg text-sm font-medium transition"
              >
                {t('dashboard.viewDetails')} →
              </button>
            </div>
          )}

          {/* Help Card */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              💡 {t('dashboard.helpSupport')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {t('dashboard.helpDesc')}
            </p>
            <button className="text-primary-600 text-sm font-medium hover:underline">
              {t('dashboard.documentation')} →
            </button>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

export default DashboardPageNew;
